"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import * as m from "motion/react-m";
import {
  AnimatePresence,
  animate,
  type MotionValue,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "motion/react";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import { Button } from "@ui/components/core/Button";
import IconChevronLeftMedium from "@ui/central-icons/icons/IconChevronLeftMedium";
import IconChevronRightMedium from "@ui/central-icons/icons/IconChevronRightMedium";
import IconCrossLarge from "@ui/central-icons/icons/IconCrossLarge";
import IconPencilLine from "@ui/central-icons/icons/IconPencilLine";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import ImageRippleFX from "@ui/effects/ImageRippleFX";
import { getButtonCenterUv } from "@ui/effects/getButtonCenterUv";
import { clamp, lerp, smoothstep } from "@ui/lib/math";

import { CreateMode, type CreateState } from "./CreateMode";
import { CreatePromptBar } from "./CreatePromptBar";
import { ElevatedButton } from "./ElevatedButton";
import { InteractionHint } from "./InteractionHint";
import { useImageAttachments } from "./useImageAttachments";
import {
  type InteractiveImageCreationResult,
  type InteractiveImageExample,
  type InteractiveImageGenerate,
  type InteractiveImageGenerationError,
  makeCrossfade,
  PILL_SHADOW,
  SCENARIO_TRANSITION,
  TIMING,
} from "./types";

export interface InteractiveImageProps {
  /** Examples cycled through by the demo (prompt + resulting image). */
  examples: InteractiveImageExample[];
  className?: string;
  /** Auto-type prompts and rotate through examples. Defaults to true. */
  autoPlay?: boolean;
  /**
   * Show a hand-drawn arrow pointing at the create CTA to signal the demo is interactive.
   * Desktop-only; defaults to false. Wired as a CMS toggle for A/B testing.
   */
  showInteractionHint?: boolean;
  /**
   * Canned results cycled in create mode when no `onGenerate` is given. Falls back to the
   * example images when omitted or empty.
   */
  creationResults?: InteractiveImageCreationResult[];
  /** Link for the post-generation "Sign up" CTA. Falls back to a non-navigating button. */
  signUpHref?: string;
  /**
   * Injected generator (keeps this component backend- and CMS-agnostic). When provided, create
   * mode generates live — first submit creates, each later submit edits the previous result —
   * instead of cycling the canned `creationResults`.
   */
  onGenerate?: InteractiveImageGenerate;
  /** A generation failure to surface in the card; owned and mapped by the producer. */
  generationError?: InteractiveImageGenerationError;
  /** Asked to clear {@link generationError} (on resubmit / re-engaging the prompt). */
  onGenerationErrorClear?: () => void;
  /** Fired when the visitor first engages the demo (opens create mode via the prompt pill or a
   *  "Create your own" CTA). Kept backend-agnostic so the producer owns analytics. */
  onEngage?: () => void;
}

const pillClassName = clsx(
  "tw-flex tw-h-11 tw-min-w-0 tw-items-center tw-rounded-full tw-bg-white tw-py-1.5 tw-pl-[1.1875rem] tw-pr-[1.1875rem]",
  PILL_SHADOW,
);

const SCENARIO_RENDER_RADIUS = 2;
const CREATE_SURFACE_SHRINK_RESPONSE = 0.35;
const PROMPT_PRESS_REVEAL_LEAD_MS = 80;
const PROMPT_PRESS_SCALE = 0.95;
const topRightButtonClassName = "tw-size-9 tw-px-0";
const topRightChevronClassName = clsx("tw-size-6 [&_path]:tw-origin-center [&_path]:tw-scale-125", iconStrokeFix);

export function InteractiveImage({
  examples,
  className,
  autoPlay = true,
  showInteractionHint = false,
  creationResults,
  signUpHref,
  onGenerate,
  generationError,
  onGenerationErrorClear,
  onEngage,
}: InteractiveImageProps) {
  const t = useTranslations("ClientKeys.InteractiveImage");
  const reduceMotion = useReducedMotion();
  const promptPressScale = useMotionValue(1);

  const count = examples.length;
  // Index of the prompt currently typing / shown.
  const [promptIndex, setPromptIndex] = useState(0);
  // Index of the displayed image; lags the prompt during auto-play so the result appears
  // after the edit is typed. Starts on example 0, making the first reveal a no-op.
  const [displayedIndex, setDisplayedIndex] = useState(0);
  const displayedScenarioGroupRef = useRef(0);
  // Opening example is already settled, so seed the typed-word count to its length to avoid
  // a type-from-empty flash.
  const [typedWords, setTypedWords] = useState(() => (autoPlay ? (examples[0]?.prompt.split(" ").length ?? 0) : 0));
  // Prev/next arrows pause autoplay until the scenario strip settles.
  const [demoPaused, setDemoPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [createPhase, setCreatePhase] = useState<"closed" | "open" | "closing">("closed");
  const [createState, setCreateState] = useState<CreateState>("idle");
  // -1 before the first result; each submit advances (and cycles) through the set.
  const [resultIndex, setResultIndex] = useState(-1);
  const [loadedResultIndex, setLoadedResultIndex] = useState<number | null>(null);
  // When true, the prompt bar surfaces the result as the edit source chip.
  const [editingResult, setEditingResult] = useState(false);
  const [promptText, setPromptText] = useState("");
  const attachments = useImageAttachments();

  // Live generation (when `onGenerate` is provided; canned path uses neither ref). abortRef
  // cancels the in-flight request; reqIdRef guards against a stale resolve landing late.
  const [liveResults, setLiveResults] = useState<InteractiveImageCreationResult[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const reqIdRef = useRef(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const promptButtonRef = useRef<HTMLButtonElement>(null);
  // The prompt pill: the ripple originates from its centre, so an edit reads as flowing out
  // of where the prompt was typed.
  const promptBarRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const generateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const promptPressAnimationRef = useRef<ReturnType<typeof animate> | null>(null);
  // Buttons update this target directly; the rendered strip position aims toward it.
  const [scenarioTargetIndex, setScenarioTargetIndex] = useState(0);
  const scenarioTargetIndexRef = useRef(0);
  const resumePromptIndexRef = useRef<number | null>(null);
  // The first auto-play cycle skips typing/reveal (opening image + prompt are already shown)
  // and only holds before advancing.
  const initialRef = useRef(true);

  const results: InteractiveImageCreationResult[] = onGenerate
    ? liveResults
    : creationResults && creationResults.length > 0
      ? creationResults
      : examples;
  const resultsCount = results.length;
  const currentResult = resultIndex >= 0 ? results[resultIndex] : undefined;
  // A result has been revealed at least once. Derived from resultIndex so the two can't drift.
  const hasResult = resultIndex >= 0;
  const resultImageLoaded = hasResult && loadedResultIndex === resultIndex;

  const current = examples[promptIndex];
  const words = useMemo(() => current?.prompt.split(" ") ?? [], [current?.prompt]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0);
      },
      { threshold: 0.05 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // First index of each scenario group (a contiguous run sharing one `scenario`); the prev/next
  // arrows jump between whole scenarios, not the edits within one.
  const scenarioStarts = useMemo(
    () => examples.map((_, i) => i).filter(i => i === 0 || examples[i].scenario !== examples[i - 1]?.scenario),
    [examples],
  );
  const scenarioCount = scenarioStarts.length;

  // Group of the currently displayed image. The autoplay switch compares this against the prompt's
  // group (not raw scenario strings) so the strip advances by whole scenarios even when
  // non-adjacent groups happen to share a `scenario` key.
  useEffect(() => {
    displayedScenarioGroupRef.current = getScenarioGroup(scenarioStarts, displayedIndex);
  }, [displayedIndex, scenarioStarts]);
  const setScenarioTargetIndexValue = useCallback((position: number) => {
    scenarioTargetIndexRef.current = position;
    setScenarioTargetIndex(position);
  }, []);
  const resumeDemoFromScenarioIdle = useCallback(() => {
    const resumeIndex = resumePromptIndexRef.current;
    if (resumeIndex === null) return;
    resumePromptIndexRef.current = null;
    initialRef.current = false;
    setTypedWords(0);
    setPromptIndex(resumeIndex);
    setDemoPaused(false);
  }, []);
  const pressPromptPill = useCallback(() => {
    if (reduceMotion) return;
    promptPressAnimationRef.current?.stop();

    const down = animate(promptPressScale, PROMPT_PRESS_SCALE, { duration: 0.12, ease: "easeOut" });
    promptPressAnimationRef.current = down;
    down.finished
      .then(() => {
        const up = animate(promptPressScale, 1, { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const });
        promptPressAnimationRef.current = up;
        return up.finished;
      })
      .catch(() => undefined);
  }, [promptPressScale, reduceMotion]);

  const createActive = createPhase !== "closed";
  const createTarget = createPhase === "open" ? 1 : 0;
  const createTravelScaleTarget = createTarget === 1 ? SCENARIO_TRANSITION.MOVING_SCALE : 1;
  // Visibility only pauses the autoplay clock; explicit freezes still sync image + prompt.
  const isFrozen = !autoPlay || !!reduceMotion || demoPaused || createActive;
  const isAutoPlayFrozen = isFrozen || !isVisible;
  // When frozen the image always matches the prompt; during playback it lags behind.
  const shownImageIndex = isFrozen ? promptIndex : displayedIndex;
  const displayedWords = isFrozen ? words : words.slice(0, typedWords);
  const canSubmit = promptText.trim().length > 0 || attachments.files.length > 0;
  const isGenerating = createState === "generating";
  // Editing a result surfaces it as the prompt source chip; the image itself stays full-frame
  // until a new generation starts.
  const collapsed = hasResult && editingResult;
  // The download CTA claims the displayed result; the canned (no-backend) path carries no claim
  // URL, so it falls back to the static sign-up link.
  const resultClaimHref = currentResult?.claimUrl ?? signUpHref;
  const createSurfaceScaleTarget =
    createPhase === "open" && createState === "result" && resultImageLoaded && !generationError
      ? 1
      : SCENARIO_TRANSITION.MOVING_SCALE;
  const createCardScale = useAimedCardScale(createSurfaceScaleTarget, !!reduceMotion, CREATE_SURFACE_SHRINK_RESPONSE);
  const createImageScale = useTransform(createCardScale, getCardImageScale);
  const createContentScale = useTransform(createCardScale, value => 1 / value);

  // Auto-play sequence: type the prompt, hold, reveal the result, hold, advance.
  useEffect(() => {
    if (isAutoPlayFrozen || count === 0) return;
    const displayedGroup = displayedScenarioGroupRef.current;
    const promptGroup = getScenarioGroup(scenarioStarts, promptIndex);
    const isScenarioSwitchReveal = displayedGroup !== promptGroup;

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const wait = (ms: number) =>
      new Promise<void>(resolve => {
        timers.push(setTimeout(resolve, ms));
      });

    (async () => {
      // Opening cycle: image + full prompt are already on screen, so just hold before advancing.
      if (initialRef.current) {
        initialRef.current = false;
        await wait(TIMING.HOLD_AFTER_RESULT_MS);
        if (cancelled) return;
        if (count > 1) setPromptIndex(prev => (prev + 1) % count);
        return;
      }

      setTypedWords(0);
      for (let i = 1; i <= words.length; i++) {
        await wait(TIMING.WORD_INTERVAL_MS);
        if (cancelled) return;
        setTypedWords(i);
      }
      // A within-scenario edit holds before its ripple reveal; a scenario switch retargets the
      // strip the instant the prompt is typed (no pause).
      if (!isScenarioSwitchReveal) {
        await wait(TIMING.HOLD_BEFORE_RESULT_MS);
        if (cancelled) return;
      }
      if (isScenarioSwitchReveal && scenarioCount > 1) {
        // Advance forward to the prompt's group (wrapping), keeping the unbounded target so the
        // prev/next arrows' relative math stays valid.
        const forward = wrapIndex(promptGroup - displayedGroup, scenarioCount);
        setScenarioTargetIndexValue(scenarioTargetIndexRef.current + forward);
      }
      setDisplayedIndex(promptIndex);
      await wait(TIMING.HOLD_AFTER_RESULT_MS);
      if (cancelled) return;
      // A single example has nowhere to advance to; re-setting the same index would never
      // retrigger this effect.
      if (count > 1) setPromptIndex(prev => (prev + 1) % count);
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      promptPressAnimationRef.current?.stop();
      promptPressScale.set(1);
    };
  }, [
    promptIndex,
    words,
    count,
    isAutoPlayFrozen,
    scenarioStarts,
    scenarioCount,
    promptPressScale,
    setScenarioTargetIndexValue,
  ]);

  // Clear any in-flight canned-generation timer and abort any live request on unmount.
  useEffect(
    () => () => {
      if (generateTimerRef.current) clearTimeout(generateTimerRef.current);
      abortRef.current?.abort();
    },
    [],
  );

  // Tear down any in-flight generation, canned or live: clear the canned timer, abort the live
  // request, and bump the request id so a late resolve/reject is ignored. Each half is a no-op on
  // the path that isn't active.
  const clearGeneration = () => {
    if (generateTimerRef.current) {
      clearTimeout(generateTimerRef.current);
      generateTimerRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
    reqIdRef.current++;
  };

  const stopAndGo = (index: number, scenarioPosition: number) => {
    resumePromptIndexRef.current = autoPlay && !reduceMotion && createPhase === "closed" ? (index + 1) % count : null;
    setDemoPaused(true);
    setScenarioTargetIndexValue(scenarioPosition);
    setPromptIndex(index);
    setDisplayedIndex(index);
  };
  // Land on the first image of the target scenario group (wrapping around).
  const goToScenario = (scenarioPosition: number) => {
    const start = scenarioStarts[wrapIndex(scenarioPosition, scenarioCount)];
    if (start === undefined) return;
    stopAndGo(start, scenarioPosition);
  };
  const goPrev = () => {
    goToScenario(scenarioTargetIndexRef.current - 1);
  };
  const goNext = () => {
    goToScenario(scenarioTargetIndexRef.current + 1);
  };

  // Tear down any in-flight generation and reset create-mode state to its empty baseline.
  // The vertical layer itself is owned separately so close can slide down before cleanup.
  const resetCreateMode = () => {
    clearGeneration();
    onGenerationErrorClear?.();
    setCreateState("idle");
    setEditingResult(false);
    setResultIndex(-1);
    setLoadedResultIndex(null);
    setLiveResults([]);
    setPromptText("");
    attachments.reset();
  };

  const enterCreate = () => {
    onEngage?.();
    const nextPromptIndex = count > 1 ? (shownImageIndex + 1) % count : null;
    resetCreateMode();
    resumePromptIndexRef.current = autoPlay && !reduceMotion ? nextPromptIndex : null;
    setDemoPaused(true);
    setPromptIndex(shownImageIndex);
    setDisplayedIndex(shownImageIndex);
    setCreatePhase("open");
  };

  const exitCreate = () => {
    clearGeneration();
    onGenerationErrorClear?.();
    setCreatePhase("closing");
    // Return focus to the prompt that opened create mode.
    requestAnimationFrame(() => promptButtonRef.current?.focus());
  };

  const handleCreateTrackIdle = () => {
    if (createPhase === "open") {
      textareaRef.current?.focus();
      return;
    }
    if (createPhase !== "closing") return;
    setCreatePhase("closed");
    resetCreateMode();
    if (resumePromptIndexRef.current === null) setDemoPaused(false);
    else resumeDemoFromScenarioIdle();
  };

  // The scenario strip can settle after create mode has opened over it (a nav click immediately
  // followed by entering create). That resume belongs to handleCreateTrackIdle, so swallow the
  // strip's idle while create is active — otherwise it advances the demo and consumes the
  // post-create resume under the overlay.
  const handleScenarioIdle = useCallback(() => {
    if (createActive) return;
    resumeDemoFromScenarioIdle();
  }, [createActive, resumeDemoFromScenarioIdle]);

  const { position: createPosition, scale: createTravelScale } = useAimedTrackPosition(
    createTarget,
    !!reduceMotion,
    handleCreateTrackIdle,
    createTravelScaleTarget,
  );
  const createTrackY = useTransform(createPosition, value => `${value * -100}%`);

  // Shared by the live and canned paths. Scales any existing result into the inset card before the
  // "generating…" grid crossfades in there (no-op on the first generation), then enters generating.
  const beginGenerating = () => {
    if (hasResult) setEditingResult(true);
    setCreateState("generating");
  };
  // Reveal a freshly produced result: leave edit mode, clear the prompt + attachments for the next
  // edit, and switch to the result state. Callers bump the result index first.
  const finishWithResult = () => {
    setEditingResult(false);
    setPromptText("");
    attachments.reset();
    setCreateState("result");
  };

  const submit = () => {
    if (isGenerating || !canSubmit) return;
    onGenerationErrorClear?.();

    // Live generation: first submit creates, each later submit edits the previous result
    // (chained via its opaque assetId). Results are appended, not cycled.
    if (onGenerate) {
      clearGeneration();
      const controller = new AbortController();
      abortRef.current = controller;
      const reqId = ++reqIdRef.current;
      beginGenerating();
      onGenerate({
        prompt: promptText.trim(),
        attachments: attachments.rawFiles,
        baseImageAssetId: currentResult?.assetId,
        signal: controller.signal,
      })
        .then(result => {
          // Drop a stale resolve (superseded or cancelled).
          if (controller.signal.aborted || reqId !== reqIdRef.current) return;
          setLiveResults(prev => [...prev, result]);
          setResultIndex(prev => prev + 1);
          finishWithResult();
        })
        .catch(() => {
          if (controller.signal.aborted || reqId !== reqIdRef.current) return;
          // Keep the inset card on screen so the producer-set generationError renders there;
          // keep the prompt + attachment for a retry.
          setCreateState(hasResult ? "result" : "idle");
          setEditingResult(hasResult);
        });
      return;
    }

    clearGeneration();
    beginGenerating();
    generateTimerRef.current = setTimeout(() => {
      setResultIndex(prev => (prev + 1) % resultsCount);
      finishWithResult();
    }, TIMING.GENERATE_MS);
  };

  const stop = () => {
    clearGeneration();
    setCreateState(hasResult ? "result" : "idle");
  };

  const enterResultEditing = () => {
    onGenerationErrorClear?.();
    if (hasResult) setEditingResult(true);
  };

  const handlePromptChange = (value: string) => {
    setPromptText(value);
    enterResultEditing();
  };

  // Nothing to show without examples — bail after all hooks have run.
  if (count === 0) return null;

  return (
    <div
      ref={rootRef}
      className={clsx(
        "tw-relative tw-aspect-[3/4] tw-w-full tw-overflow-hidden tw-rounded-3xl tw-border-0.5 tw-border-black/20 tw-bg-cream md:tw-aspect-[1176/583]",
        className,
      )}
    >
      <m.div className='tw-absolute tw-inset-0' style={{ y: createTrackY }}>
        <div className='tw-absolute tw-inset-0'>
          <ImageStage
            examples={examples}
            index={shownImageIndex}
            scenarioStarts={scenarioStarts}
            scenarioTargetIndex={scenarioTargetIndex}
            reduceMotion={!!reduceMotion}
            centerRef={promptBarRef}
            onScenarioIdle={handleScenarioIdle}
            onRevealStart={pressPromptPill}
            travelScale={createTravelScale}
          />

          <div className='tw-absolute tw-inset-x-1.5 tw-bottom-1.5 tw-z-10 tw-flex tw-items-center tw-justify-center tw-gap-1.5 md:tw-inset-x-4 md:tw-bottom-5 md:tw-gap-2'>
            <m.div
              ref={promptBarRef}
              className={clsx(pillClassName, "tw-w-[28.75rem] tw-max-w-full tw-origin-center tw-will-change-transform")}
              initial={false}
              style={{ scale: promptPressScale }}
            >
              <button
                ref={promptButtonRef}
                type='button'
                onClick={enterCreate}
                aria-label={t("edit_prompt")}
                className='tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-rounded-full tw-text-left tw-text-black tw-type-sm tw-outline-none focus-visible:tw-outline-focus'
              >
                {/* Presentational only — the streaming text changes word-by-word, so the
                    accessible name comes from aria-label instead. */}
                <span aria-hidden className='tw-truncate'>
                  {displayedWords.join(" ")}
                </span>
              </button>
            </m.div>

            <Button
              size='lg-compact'
              variant='elevated'
              aria-label={t("create_your_own")}
              icon={<IconPencilLine className={clsx(iconStrokeFix, "tw-size-3.5")} />}
              onClick={enterCreate}
              className='!tw-size-11 !tw-px-0 tw-shrink-0 md:tw-hidden'
            />
            <div className='tw-relative tw-hidden tw-shrink-0 md:tw-flex'>
              {showInteractionHint && (
                <InteractionHint
                  isVisible={isVisible}
                  reduceMotion={!!reduceMotion}
                  className='tw-absolute tw-inset-x-0 tw-bottom-full tw-mb-1.5'
                />
              )}
              <ElevatedButton onClick={enterCreate}>{t("create_your_own")}</ElevatedButton>
            </div>
          </div>
        </div>

        <div
          aria-hidden={!createActive}
          inert={createPhase !== "open" ? true : undefined}
          className={clsx("tw-absolute tw-inset-0", createPhase !== "open" && "tw-pointer-events-none")}
          style={{ transform: "translateY(100%)" }}
        >
          <CreateMode
            createState={createState}
            cardScale={createCardScale}
            contentScale={createContentScale}
            imageScale={createImageScale}
            hasResult={hasResult}
            result={currentResult}
            resultKey={resultIndex}
            resultLoaded={resultImageLoaded}
            reduceMotion={!!reduceMotion}
            signUpHref={signUpHref}
            generationError={generationError}
            // Records the key that actually loaded (not the latest resultIndex) so a late onLoad from
            // a superseded result can't mark a newer, not-yet-ready one as loaded.
            onResultLoad={setLoadedResultIndex}
            onRetry={submit}
          />

          <div className='tw-absolute tw-inset-x-1.5 tw-bottom-1.5 tw-z-10 tw-flex tw-justify-center md:tw-inset-x-4 md:tw-bottom-5'>
            <CreatePromptBar
              promptText={promptText}
              onPromptChange={handlePromptChange}
              onPromptFocus={enterResultEditing}
              isGenerating={isGenerating}
              hasResult={hasResult}
              canSubmit={canSubmit}
              onSubmit={submit}
              onStop={stop}
              onClose={exitCreate}
              // Editing surfaces the result as the source media in the prompt bar; removing
              // that chip resets create mode to empty.
              editingSourceUrl={collapsed ? currentResult?.imageUrl : undefined}
              onRemoveEditingSource={resetCreateMode}
              files={attachments.files}
              onAddFiles={attachments.add}
              onRemoveFile={attachments.remove}
              canAttach={attachments.files.length < attachments.maxFiles}
              showDownloadCta={resultImageLoaded && !isGenerating}
              signUpHref={resultClaimHref}
              reduceMotion={!!reduceMotion}
              textareaRef={textareaRef}
            />
          </div>
        </div>
      </m.div>

      {/* Manual navigation shares the top-right slot with create close. */}
      {createPhase === "open" ? (
        <Button
          size='md-compact'
          variant='elevated'
          aria-label={t("close")}
          icon={<IconCrossLarge className={clsx(iconStrokeFix, "tw-size-4")} />}
          onClick={exitCreate}
          className={clsx(topRightButtonClassName, "tw-absolute tw-right-1.5 tw-top-1.5 tw-z-20")}
        />
      ) : (
        scenarioCount > 1 && (
          <div className='tw-absolute tw-right-1.5 tw-top-1.5 tw-z-10 tw-flex tw-gap-1.5'>
            <Button
              size='md-compact'
              variant='elevated'
              aria-label={t("prev_example")}
              icon={<IconChevronLeftMedium className={topRightChevronClassName} />}
              onClick={goPrev}
              className={topRightButtonClassName}
            />
            <Button
              size='md-compact'
              variant='elevated'
              aria-label={t("next_example")}
              icon={<IconChevronRightMedium className={topRightChevronClassName} />}
              onClick={goNext}
              className={topRightButtonClassName}
            />
          </div>
        )
      )}
    </div>
  );
}

/**
 * Renders the image area: same-scenario edits use the ripple/crossfade path, while scenario
 * navigation is a persistent index-driven track (see {@link SCENARIO_TRANSITION}).
 */
function ImageStage({
  examples,
  index,
  scenarioStarts,
  scenarioTargetIndex,
  reduceMotion,
  centerRef,
  onScenarioIdle,
  onRevealStart,
  travelScale,
}: {
  examples: InteractiveImageExample[];
  index: number;
  scenarioStarts: number[];
  scenarioTargetIndex: number;
  reduceMotion: boolean;
  /** Element the ripple originates from (the prompt pill); falls back to frame centre. */
  centerRef: React.RefObject<HTMLDivElement | null>;
  onScenarioIdle: () => void;
  onRevealStart: () => void;
  travelScale: MotionValue<number>;
}) {
  const [scenarioImages, setScenarioImages] = useState(() => scenarioStarts);
  // True while the WebGL ripple-reveal between two same-scenario images is playing.
  const [rippling, setRippling] = useState(false);
  const prevIndexRef = useRef(index);
  // The incoming index a ripple targets; advanced onto the (hidden) base layer only once the
  // ripple starts — see the mount effect's onStart.
  const pendingIndexRef = useRef(index);
  const pendingScenarioRef = useRef<number | null>(getScenarioGroup(scenarioStarts, index));
  const glContainerRef = useRef<HTMLDivElement>(null);
  const fxRef = useRef<ImageRippleFX | null>(null);
  const onRevealStartRef = useRef(onRevealStart);
  const revealDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setScenarioImage = useCallback((scenario: number, imageIndex: number) => {
    setScenarioImages(prev => replaceAt(prev, scenario, imageIndex));
  }, []);
  const clearRevealDelay = useCallback(() => {
    if (!revealDelayRef.current) return;
    clearTimeout(revealDelayRef.current);
    revealDelayRef.current = null;
  }, []);

  useEffect(() => {
    onRevealStartRef.current = onRevealStart;
  }, [onRevealStart]);

  useEffect(() => {
    setScenarioImages(prev => {
      const next = scenarioStarts.map((start, scenario) => {
        const current = prev[scenario];
        return current !== undefined && examples[current] ? current : start;
      });
      return sameNumbers(prev, next) ? prev : next;
    });
  }, [examples, scenarioStarts]);

  // Create the WebGL ripple effect once (unless reduced motion); it renders only while a
  // same-scenario transition plays. onStart advances the base layer behind the ripple canvas so
  // the base never crossfades ahead of the ripple (which would flash backward). Disposed on
  // unmount (Strict Mode safe); an unsupported context falls back to the crossfade below.
  useEffect(() => {
    if (reduceMotion) return;
    const container = glContainerRef.current;
    if (!container) return;
    const fx = new ImageRippleFX(container, {
      onStart: () => {
        if (pendingScenarioRef.current === null) return;
        setRippling(true);
        setScenarioImage(pendingScenarioRef.current, pendingIndexRef.current);
      },
      onDone: () => {
        if (pendingScenarioRef.current !== null) setRippling(false);
      },
      // Load failed → no ripple, so advance the base layer (plain crossfade).
      onError: () => {
        if (pendingScenarioRef.current === null) return;
        setRippling(false);
        setScenarioImage(pendingScenarioRef.current, pendingIndexRef.current);
      },
    });
    if (!fx.isSupported) {
      fx.dispose();
      return;
    }
    fxRef.current = fx;
    return () => {
      clearRevealDelay();
      fx.dispose();
      fxRef.current = null;
      setRippling(false);
    };
  }, [clearRevealDelay, reduceMotion, setScenarioImage]);

  useEffect(() => {
    clearRevealDelay();
    const from = prevIndexRef.current;
    if (from === index) return;
    prevIndexRef.current = index;

    const fromScenario = getScenarioGroup(scenarioStarts, from);
    const toScenario = getScenarioGroup(scenarioStarts, index);
    const isScenarioSwitch = fromScenario !== toScenario;

    if (isScenarioSwitch) {
      pendingScenarioRef.current = null;
      onRevealStartRef.current();
      setRippling(false);
      setScenarioImage(toScenario, index);
      return;
    }

    if (!isScenarioSwitch && fxRef.current) {
      // Same scenario → ripple-reveal. Keep the base on the previous image until onStart
      // advances it behind the canvas, so it never crossfades ahead of the ripple.
      pendingIndexRef.current = index;
      pendingScenarioRef.current = toScenario;
      // Originate the ripple from the prompt pill's centre so the edit reads as flowing out of
      // the input; getButtonCenterUv falls back to the frame centre when either is unmeasured.
      onRevealStartRef.current();
      const center = getButtonCenterUv(centerRef.current, glContainerRef.current);
      revealDelayRef.current = setTimeout(() => {
        revealDelayRef.current = null;
        fxRef.current?.transition(examples[from].imageUrl, examples[index].imageUrl, center);
      }, PROMPT_PRESS_REVEAL_LEAD_MS);
    } else {
      // Reduced motion or no WebGL → plain opacity crossfade.
      onRevealStartRef.current();
      setScenarioImage(toScenario, index);
    }
  }, [clearRevealDelay, index, reduceMotion, examples, centerRef, scenarioStarts, setScenarioImage]);

  const rippleImageScale = useTransform(travelScale, getCardImageScale);

  return (
    <>
      {/* Settled / editing images. Hidden behind the ripple canvas during a same-scenario reveal;
          scenario movement itself is just the strip transform below. */}
      <div className={clsx("tw-absolute tw-inset-0", rippling && "tw-invisible")}>
        <ScenarioTrack
          examples={examples}
          scenarioStarts={scenarioStarts}
          scenarioImages={scenarioImages}
          targetPosition={scenarioTargetIndex}
          reduceMotion={reduceMotion}
          onIdle={onScenarioIdle}
          travelScale={travelScale}
        />
      </div>

      {/* WebGL ripple-reveal overlay for same-scenario edits. Always mounted so the effect can
          own its canvas; only visible while rippling. */}
      <div className={clsx("tw-absolute tw-inset-0", !rippling && "tw-invisible")}>
        <m.div className='tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-2.5xl' style={{ scale: travelScale }}>
          <m.div className='tw-absolute tw-inset-0' style={{ scale: rippleImageScale }}>
            <div ref={glContainerRef} className='tw-absolute tw-inset-0' />
          </m.div>
        </m.div>
      </div>
    </>
  );
}

/**
 * Scenario cards sit on one strip. The aimed position owns the transform, so any new click
 * updates the target instead of restarting a tween or being dropped mid-slide.
 */
function ScenarioTrack({
  examples,
  scenarioStarts,
  scenarioImages,
  targetPosition,
  reduceMotion,
  onIdle,
  travelScale,
}: {
  examples: InteractiveImageExample[];
  scenarioStarts: number[];
  scenarioImages: number[];
  targetPosition: number;
  reduceMotion: boolean;
  onIdle: () => void;
  travelScale: MotionValue<number>;
}) {
  const scenarioCount = scenarioStarts.length;
  const { position, scale, visibleSlot } = useAimedTrackPosition(targetPosition, reduceMotion, onIdle);
  const x = useTransform(position, value => `${value * -100}%`);
  const cardScale = useTransform([scale, travelScale], ([trackScale, movingScale]: number[]) =>
    Math.min(trackScale, movingScale),
  );
  const imageScale = useTransform(cardScale, getCardImageScale);
  const renderRadius = scenarioCount > 1 ? SCENARIO_RENDER_RADIUS : 0;
  const minPosition = visibleSlot - renderRadius;
  const maxPosition = visibleSlot + renderRadius;
  const positions = Array.from({ length: maxPosition - minPosition + 1 }, (_, i) => minPosition + i);

  if (scenarioCount === 0) return null;

  // The strip owns translation; cards scale inside their own slots from a separate scale spring.
  return (
    <m.div className='tw-absolute tw-inset-0' style={{ x }}>
      {positions.map(position => {
        const scenario = wrapIndex(position, scenarioCount);
        const imageIndex = scenarioImages[scenario] ?? scenarioStarts[scenario];
        const example = examples[imageIndex];
        if (!example) return null;

        return (
          <ScenarioCard
            key={position}
            example={example}
            imageIndex={imageIndex}
            position={position}
            priority={position === targetPosition && imageIndex === 0}
            reduceMotion={reduceMotion}
            scale={cardScale}
            imageScale={imageScale}
          />
        );
      })}
    </m.div>
  );
}

function useAimedTrackPosition(target: number, reduceMotion: boolean, onIdle: () => void, settledScale = 1) {
  const position = useMotionValue(target);
  const scale = useMotionValue(settledScale);
  const targetRef = useRef(target);
  const settledScaleRef = useRef(settledScale);
  const onIdleRef = useRef(onIdle);
  const frameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const rampStartRef = useRef<number | null>(null);
  const velocityRef = useRef(0);
  const scaleVelocityRef = useRef(0);
  const [visibleSlot, setVisibleSlot] = useState(target);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    const stop = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      lastTimeRef.current = null;
      rampStartRef.current = null;
    };

    const tick = (time: number) => {
      const lastTime = lastTimeRef.current ?? time;
      const dt = Math.min((time - lastTime) / 1000, 0.05);
      const current = position.get();
      const delta = targetRef.current - current;
      const distance = Math.abs(delta);
      const scaleDistance = smoothstep(clamp(distance / SCENARIO_TRANSITION.SCALE_DISTANCE, 0, 1));
      const targetScale = lerp(settledScaleRef.current, SCENARIO_TRANSITION.MOVING_SCALE, scaleDistance);
      const currentScale = scale.get();
      const scaleDelta = targetScale - currentScale;
      const scaleStiffness = getCardScaleStiffness(currentScale, targetScale);
      const springPull = getSoftStartPull(time, rampStartRef);

      lastTimeRef.current = time;

      if (
        distance < 0.001 &&
        Math.abs(velocityRef.current) < 0.001 &&
        Math.abs(scaleDelta) < 0.001 &&
        Math.abs(scaleVelocityRef.current) < 0.001
      ) {
        position.set(targetRef.current);
        velocityRef.current = 0;
        scale.set(settledScaleRef.current);
        scaleVelocityRef.current = 0;
        setVisibleSlot(Math.round(targetRef.current));
        stop();
        onIdleRef.current();
        return;
      }

      velocityRef.current +=
        (delta * SCENARIO_TRANSITION.SPRING_STIFFNESS * springPull -
          velocityRef.current * SCENARIO_TRANSITION.SPRING_DAMPING) *
        dt;
      const next = current + velocityRef.current * dt;
      position.set(next);
      scaleVelocityRef.current +=
        (scaleDelta * scaleStiffness - scaleVelocityRef.current * SCENARIO_TRANSITION.SCALE_DAMPING) * dt;
      scale.set(clamp(currentScale + scaleVelocityRef.current * dt, SCENARIO_TRANSITION.MOVING_SCALE, 1));
      setVisibleSlot(prev => {
        const rounded = Math.round(next);
        return rounded === prev ? prev : rounded;
      });
      frameRef.current = requestAnimationFrame(tick);
    };

    targetRef.current = target;
    settledScaleRef.current = settledScale;

    if (reduceMotion) {
      stop();
      position.set(target);
      velocityRef.current = 0;
      scale.set(settledScale);
      scaleVelocityRef.current = 0;
      setVisibleSlot(target);
      onIdleRef.current();
      return;
    }

    if (frameRef.current === null) frameRef.current = requestAnimationFrame(tick);
  }, [position, reduceMotion, scale, settledScale, target]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return { position, scale, visibleSlot };
}

function useAimedCardScale(target: number, reduceMotion: boolean, shrinkResponse = 1) {
  const scale = useMotionValue(target);
  const targetRef = useRef(target);
  const frameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const rampStartRef = useRef<number | null>(null);
  const velocityRef = useRef(0);

  useEffect(() => {
    const stop = () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      lastTimeRef.current = null;
      rampStartRef.current = null;
    };

    const tick = (time: number) => {
      const lastTime = lastTimeRef.current ?? time;
      const dt = Math.min((time - lastTime) / 1000, 0.05);
      const current = scale.get();
      const delta = targetRef.current - current;
      const springPull = getSoftStartPull(time, rampStartRef);

      lastTimeRef.current = time;

      if (Math.abs(delta) < 0.001 && Math.abs(velocityRef.current) < 0.001) {
        scale.set(targetRef.current);
        velocityRef.current = 0;
        stop();
        return;
      }

      velocityRef.current +=
        (delta * getCardScaleStiffness(current, targetRef.current, shrinkResponse) * springPull -
          velocityRef.current * SCENARIO_TRANSITION.SCALE_DAMPING) *
        dt;
      scale.set(clamp(current + velocityRef.current * dt, SCENARIO_TRANSITION.MOVING_SCALE, 1));
      frameRef.current = requestAnimationFrame(tick);
    };

    targetRef.current = target;

    if (reduceMotion) {
      stop();
      scale.set(target);
      velocityRef.current = 0;
      return;
    }

    if (frameRef.current === null) frameRef.current = requestAnimationFrame(tick);
  }, [reduceMotion, scale, shrinkResponse, target]);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return scale;
}

function ScenarioCard({
  example,
  imageIndex,
  position,
  priority,
  reduceMotion,
  scale,
  imageScale,
}: {
  example: InteractiveImageExample;
  imageIndex: number;
  position: number;
  priority: boolean;
  reduceMotion: boolean;
  scale: MotionValue<number>;
  imageScale: MotionValue<number>;
}) {
  const crossfade = makeCrossfade(reduceMotion);

  return (
    <div className='tw-absolute tw-inset-0' style={{ transform: `translateX(${position * 100}%)` }}>
      <m.div className='tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-2.5xl' style={{ scale }}>
        <AnimatePresence initial={false}>
          <m.div
            key={imageIndex}
            className='tw-absolute tw-inset-0'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ scale: imageScale }}
            transition={crossfade}
          >
            <ExampleImage example={example} priority={priority} />
          </m.div>
        </AnimatePresence>
      </m.div>
    </div>
  );
}

function ExampleImage({ example, priority }: { example: InteractiveImageExample; priority?: boolean }) {
  return (
    <Image
      src={example.imageUrl}
      alt={example.alt ?? example.prompt}
      fill
      draggable={false}
      className='tw-select-none tw-object-cover'
      sizes='(max-width: 64rem) 100vw, 73.5rem'
      placeholder={example.blurDataURL ? "blur" : "empty"}
      blurDataURL={example.blurDataURL}
      priority={priority}
    />
  );
}

function getCardImageScale(scale: number) {
  return 1 / (scale * 0.75 + 0.25);
}

function getScenarioGroup(scenarioStarts: number[], index: number) {
  return scenarioStarts.reduce((group, start, scenario) => (start <= index ? scenario : group), 0);
}

function wrapIndex(index: number, count: number) {
  if (count === 0) return 0;
  return ((index % count) + count) % count;
}

function replaceAt(values: number[], index: number, value: number) {
  if (values[index] === value) return values;
  return values.map((item, i) => (i === index ? value : item));
}

function sameNumbers(a: number[], b: number[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function getCardScaleStiffness(currentScale: number, targetScale: number, shrinkResponse = 1) {
  const shrinkWeight = smoothstep(clamp((currentScale - targetScale) / SCENARIO_TRANSITION.SCALE_RESPONSE_RANGE, 0, 1));
  return lerp(
    SCENARIO_TRANSITION.SCALE_UP_STIFFNESS,
    SCENARIO_TRANSITION.SCALE_DOWN_STIFFNESS,
    shrinkWeight * shrinkResponse,
  );
}

function getSoftStartPull(time: number, rampStartRef: React.MutableRefObject<number | null>) {
  rampStartRef.current ??= time;
  return smoothstep(Math.min((time - rampStartRef.current) / SCENARIO_TRANSITION.SOFT_START_MS, 1));
}
